import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { hasAcademyAccess } from "@/lib/academyAccess";
import { money, btnBrand } from "@/lib/academyFormat";
import StudentModal from "@/components/academy/StudentModal";
import { ACADEMY } from "@/constants/testIds";

// Fix U (2026-10-13): alumnos de la academia (cada perfil ve los suyos).
export default function AcademyStudentsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyDebt, setOnlyDebt] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const canCreate = hasAcademyAccess(user, ["executive", "academic", "reception"]);

  useEffect(() => {
    api.get("/academy/students")
      .then(({ data }) => setStudents(data))
      .catch((err) => toast({ title: "Error al cargar alumnos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [toast]);

  const term = q.trim().toLowerCase();
  const visible = students.filter((s) => (!onlyDebt || s.balance > 0)
    && (!term || [s.name, s.email, s.phone].some((v) => (v || "").toLowerCase().includes(term))));

  return (
    <div className="space-y-6" data-testid={ACADEMY.studentsPage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Alumnos</h1>
          <p className="text-base text-muted-foreground mt-1">Inscripciones, pagos y saldo de cada alumno.</p>
        </div>
        {canCreate && (
          <Button className={btnBrand} onClick={() => setModalOpen(true)} data-testid={ACADEMY.studentsAddButton}>
            <Plus className="w-4 h-4" /> Nuevo alumno
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email o teléfono" className="max-w-sm bg-white" data-testid={ACADEMY.studentsSearchInput} />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={onlyDebt} onChange={(e) => setOnlyDebt(e.target.checked)} data-testid={ACADEMY.studentsDebtFilter} />
          Solo con saldo pendiente
        </label>
      </div>
      {loading ? (
        <p className="text-center text-muted-foreground py-12">Cargando...</p>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={ACADEMY.studentsEmpty}>
          <GraduationCap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No hay alumnos para mostrar.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid={ACADEMY.studentsTable}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                <th className="text-left font-medium py-3 px-4">Alumno</th>
                <th className="text-left font-medium py-3 px-3 hidden md:table-cell">Contacto</th>
                <th className="text-left font-medium py-3 px-3">Inscripciones</th>
                <th className="text-left font-medium py-3 px-3">Saldo</th>
                <th className="text-left font-medium py-3 px-3 hidden lg:table-cell">Ejecutivo</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-background cursor-pointer" onClick={() => navigate(`/academia/alumnos/${s.id}`)} data-testid={`${ACADEMY.studentRowPrefix}${s.id}`}>
                  <td className="py-3 px-4 font-medium text-foreground">{s.name}</td>
                  <td className="py-3 px-3 hidden md:table-cell text-muted-foreground">{s.email}<br />{s.phone}</td>
                  <td className="py-3 px-3">{s.active_enrollments}</td>
                  <td className={`py-3 px-3 font-medium ${s.balance > 0 ? "text-[#A34B49]" : "text-muted-foreground"}`}>{money(s.balance)}</td>
                  <td className="py-3 px-3 hidden lg:table-cell text-muted-foreground">{s.executive_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <StudentModal open={modalOpen} onOpenChange={setModalOpen} student={null} onSaved={(s) => navigate(`/academia/alumnos/${s.id}`)} />
    </div>
  );
}
